import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import axios from 'axios'
import { useNavigate, useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  ArrowLeft,
  X,
  Save,
  Wifi,
  WifiOff,
  Loader2,
  CheckCircle2,
  PackageOpen,
  ImagePlus,
  Camera,
  Plus,
  Trash2,
  Pencil,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { usePickup } from '@/hooks/queries/usePickups'
import { usePickupRecords, pickupRecordKeys } from '@/hooks/queries/usePickupRecords'
import { pickupKeys } from '@/hooks/queries/usePickups'
import { useWasteTypes } from '@/hooks/queries/useWasteTypes'
import { useOnline } from '@/hooks/useOnline'
import { pickupRecordsApi } from '@/lib/api'
import { saveDraft, getDraftByPickupId, updateDraft, deleteDraft, getCachedPickup, getCachedWasteTypes } from '@/lib/db'
import { parseSyncError } from '@/lib/syncErrors'
import { reportSyncError } from '@/lib/reportError'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { SEGREGATION_OPTIONS, getSegregationOption } from '@/lib/segregation'
import type {
  Pickup,
  PickupPhoto,
  PickupRecord,
  PickupRecordDraftItem,
  SegregationStatus,
  WasteType,
} from '@/types'

const MAX_ITEM_PHOTOS = 12

interface LocalPhotoPreview {
  source: 'local'
  blob: Blob
  url: string
}

interface SyncedPhotoPreview {
  source: 'synced'
  id: number
  url: string
}

type PhotoPreview = LocalPhotoPreview | SyncedPhotoPreview

interface RecordFormItem extends Omit<PickupRecordDraftItem, 'photos'> {
  photos: PhotoPreview[]
}

interface RecoverableDraft {
  recordedTime: string
  finishedTime: string
  notes: string
  items: RecordFormItem[]
}

interface SubmitRecordItem {
  waste_type_id: number
  kilos: number
  segregation_status: SegregationStatus
  notes: string
}

interface SubmitRecordPayload {
  pickup_id: number
  local_uuid: string
  started_at: string
  finished_at: string
  notes: string
  items: SubmitRecordItem[]
}

type ValidRecordFormItem = RecordFormItem & { segregation_status: SegregationStatus }

interface WasteTypeCatalogGroup {
  categoryName: string
  subcategories: Array<{
    subcategoryName: string
    wasteTypes: WasteType[]
  }>
}

function buildEmptyItem(wasteTypeId: number): RecordFormItem {
  return {
    waste_type_id: wasteTypeId,
    kilos: 0,
    segregation_status: '',
    notes: '',
    photos: [],
  }
}

function formatTimeValue(value: string): string {
  if (!value) return ''

  const directTimeMatch = value.match(/^(\d{2}:\d{2})/)
  if (directTimeMatch) {
    return directTimeMatch[1]
  }

  const parsedDate = new Date(value)
  return Number.isNaN(parsedDate.getTime()) ? '' : format(parsedDate, 'HH:mm')
}

function buildPickupDateTime(date: string, time: string): string {
  return `${date}T${time}`
}

function getStoredStartValue(source: { started_at?: string; recorded_at?: string }): string {
  return source.started_at ?? source.recorded_at ?? ''
}

function isLocalPhoto(photo: PhotoPreview): photo is LocalPhotoPreview {
  return photo.source === 'local'
}

function mapSyncedPhoto(photo: PickupPhoto): SyncedPhotoPreview {
  return {
    source: 'synced',
    id: photo.id,
    url: photo.photo_url,
  }
}

function mapRecordToFormState(record: PickupRecord): RecoverableDraft {
  return {
    recordedTime: formatTimeValue(getStoredStartValue(record)),
    finishedTime: formatTimeValue(record.finished_at ?? ''),
    notes: record.notes ?? '',
    items: record.items.map((item) => ({
      waste_type_id: item.waste_type_id,
      kilos: item.kilos,
      segregation_status: item.segregation_status ?? '',
      notes: item.notes ?? '',
      photos: (item.photos ?? []).map(mapSyncedPhoto),
    })),
  }
}

function isNetworkUnavailableError(error: unknown): boolean {
  return axios.isAxiosError(error) && !error.response
}

export function RecordFormPage() {
  const navigate = useNavigate()
  const { id: pickupId } = useParams<{ id: string }>()
  const pickupIdNum = pickupId ? parseInt(pickupId, 10) : 0
  const isOnline = useOnline()
  const queryClient = useQueryClient()

  const { data: pickup, isLoading: loadingPickup, isError: pickupIsError } = usePickup(pickupIdNum)
  const { data: pickupRecords = [], isLoading: loadingPickupRecords } = usePickupRecords(
    isOnline ? pickupIdNum : undefined
  )
  const { data: wasteTypes = [] } = useWasteTypes()

  const [recordedTime, setRecordedTime] = useState(format(new Date(), 'HH:mm'))
  const [finishedTime, setFinishedTime] = useState('')
  const [notes, setNotes] = useState('')
  const [items, setItems] = useState<RecordFormItem[]>([])
  const [cachedPickup, setCachedPickup] = useState<Pickup | null>(null)
  const [loadingCachedPickup, setLoadingCachedPickup] = useState(true)
  const [cachedWasteTypes, setCachedWasteTypes] = useState<WasteType[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [savedDraftId, setSavedDraftId] = useState<number | null>(null)
  const [serverRecordId, setServerRecordId] = useState<number | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [lastSaveMode, setLastSaveMode] = useState<'online' | 'offline' | null>(null)
  const [loadingDraft, setLoadingDraft] = useState(true)
  const [recoverableDraft, setRecoverableDraft] = useState<RecoverableDraft | null>(null)
  const [recordPhotos, setRecordPhotos] = useState<SyncedPhotoPreview[]>([])
  const [wasteTypeSearch, setWasteTypeSearch] = useState('')
  const itemsRef = useRef<RecordFormItem[]>([])
  const hasInitializedForm = useRef(false)
  const notesSectionRef = useRef<HTMLDivElement | null>(null)
  const saveActionAnchorRef = useRef<HTMLDivElement | null>(null)
  const navigateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // UUID estable durante toda la vida del formulario/draft: se genera una sola vez
  // y nunca cambia entre intentos (online, fallback offline, reintentos de sync).
  const localUuidRef = useRef<string | null>(null)
  const getOrCreateLocalUuid = (): string => {
    if (!localUuidRef.current) {
      localUuidRef.current = crypto.randomUUID()
    }
    return localUuidRef.current
  }
  const [isSaveActionVisible, setIsSaveActionVisible] = useState(false)
  const [activeEditorWasteTypeId, setActiveEditorWasteTypeId] = useState<number | null>(null)

  const activePickup = pickup ?? cachedPickup
  const effectiveWasteTypes = wasteTypes.length ? wasteTypes : cachedWasteTypes
  const activeWasteTypes = effectiveWasteTypes.filter((wt) => wt.is_active !== false)
  const allowedSubcategoryIds = activePickup?.client?.waste_subcategories?.map((ws) => ws.id) ?? []
  const availableWasteTypes =
    allowedSubcategoryIds.length > 0
      ? activeWasteTypes.filter((wt) => allowedSubcategoryIds.includes(wt.waste_subcategory_id))
      : activeWasteTypes
  const wasteTypesById = Object.fromEntries(effectiveWasteTypes.map((wt) => [wt.id, wt]))
  const itemsByWasteTypeId = Object.fromEntries(items.map((item) => [item.waste_type_id, item]))
  const selectedWasteTypes = items
    .map((item) => wasteTypesById[item.waste_type_id])
    .filter((wasteType): wasteType is NonNullable<typeof wasteType> => Boolean(wasteType))
  const hasClientRestrictions = Boolean(activePickup?.client)
  const normalizedWasteTypeSearch = wasteTypeSearch.trim().toLowerCase()
  const filteredCatalogWasteTypes = availableWasteTypes.filter((wasteType) => {
    if (!normalizedWasteTypeSearch) return true

    const searchableText = [
      wasteType.name,
      wasteType.subcategory?.name,
      wasteType.subcategory?.category?.name,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    return searchableText.includes(normalizedWasteTypeSearch)
  })
  const wasteTypesCatalog = filteredCatalogWasteTypes.reduce<WasteTypeCatalogGroup[]>((groups, wasteType) => {
    const categoryName = wasteType.subcategory?.category?.name ?? 'Sin categoría'
    const subcategoryName = wasteType.subcategory?.name ?? 'Sin subcategoría'
    const existingGroup = groups.find((group) => group.categoryName === categoryName)

    if (existingGroup) {
      const existingSubcategory = existingGroup.subcategories.find(
        (subcategory) => subcategory.subcategoryName === subcategoryName
      )

      if (existingSubcategory) {
        existingSubcategory.wasteTypes.push(wasteType)
        return groups
      }

      existingGroup.subcategories.push({
        subcategoryName,
        wasteTypes: [wasteType],
      })
      return groups
    }

    groups.push({
      categoryName,
      subcategories: [
        {
          subcategoryName,
          wasteTypes: [wasteType],
        },
      ],
    })

    return groups
  }, [])

  // Fallback: carga el retiro desde IndexedDB cuando no hay conexión o la query falla.
  // Espejo del patrón usado en PickupDetailPage.
  useEffect(() => {
    if (!pickupIdNum) {
      setLoadingCachedPickup(false)
      return
    }

    // Datos online disponibles — no necesitamos la caché
    if (pickup) {
      setCachedPickup(null)
      setLoadingCachedPickup(false)
      return
    }

    // La query de red sigue en curso — esperamos su resultado
    if (isOnline && !pickupIsError) {
      return
    }

    // Offline o error de red → leer desde IndexedDB
    let cancelled = false
    setLoadingCachedPickup(true)

    getCachedPickup(pickupIdNum)
      .then((cached) => {
        if (!cancelled) setCachedPickup(cached ?? null)
      })
      .catch((err) => {
        console.error('Error cargando retiro offline:', err)
        if (!cancelled) setCachedPickup(null)
      })
      .finally(() => {
        if (!cancelled) setLoadingCachedPickup(false)
      })

    return () => {
      cancelled = true
    }
  }, [pickupIdNum, pickup, isOnline, pickupIsError])

  // Fallback: carga tipos de residuo desde IndexedDB cuando useWasteTypes no tiene datos (offline en frío).
  useEffect(() => {
    if (wasteTypes.length > 0) return

    getCachedWasteTypes()
      .then((cached) => setCachedWasteTypes(cached))
      .catch((err) => console.error('Error cargando tipos de residuo offline:', err))
  }, [wasteTypes])

  useEffect(() => {
    async function loadExistingDraft() {
      if (!pickupIdNum) {
        setLoadingDraft(false)
        return
      }

      try {
        const draft = await getDraftByPickupId(pickupIdNum)
        if (draft) {
          const draftState = {
            recordedTime: formatTimeValue(getStoredStartValue(draft)),
            finishedTime: formatTimeValue(draft.finished_at || ''),
            notes: draft.notes,
            items: draft.items.map((item) => ({
              ...item,
              notes: item.notes ?? '',
              photos: (item.photos ?? []).map((blob) => ({
                source: 'local' as const,
                blob,
                url: URL.createObjectURL(blob),
              })),
            })),
          }

          // Restaurar el UUID del draft para que los reintentos usen el mismo valor.
          // Si el draft es legacy (sin local_uuid), getOrCreateLocalUuid() genera uno nuevo.
          localUuidRef.current = draft.local_uuid ?? null

          setSavedDraftId(draft.id ?? null)
          // Si el draft ya fue sincronizado con el servidor (server_id presente),
          // restauramos el ID para que handleSave use update en vez de create.
          if (draft.server_id) {
            setServerRecordId(draft.server_id)
          }
          setRecordedTime(draftState.recordedTime)
          setFinishedTime(draftState.finishedTime)
          setNotes(draftState.notes)
          setItems(draftState.items)
          setRecordPhotos([])
          setRecoverableDraft(draftState)
          hasInitializedForm.current = true
        }
      } catch (err) {
        console.error('Error cargando draft:', err)
      } finally {
        setLoadingDraft(false)
      }
    }

    loadExistingDraft()
  }, [pickupIdNum])

  useEffect(() => {
    if (loadingDraft || loadingPickupRecords || hasInitializedForm.current) {
      return
    }

    const existingRecord = pickupRecords[0]

    if (existingRecord) {
      const formState = mapRecordToFormState(existingRecord)
      setRecordedTime(formState.recordedTime)
      setFinishedTime(formState.finishedTime)
      setNotes(formState.notes)
      setItems(formState.items)
      setServerRecordId(existingRecord.id)
      setRecordPhotos((existingRecord.photos ?? []).map(mapSyncedPhoto))
    }

    hasInitializedForm.current = true
  }, [loadingDraft, loadingPickupRecords, pickupRecords, recoverableDraft])

  useEffect(() => {
    itemsRef.current = items
  }, [items])

  useEffect(() => {
    return () => {
      itemsRef.current
        .flatMap((item) => item.photos)
        .filter(isLocalPhoto)
        .forEach((photo) => URL.revokeObjectURL(photo.url))
    }
  }, [])

  useEffect(() => {
    return () => {
      if (navigateTimerRef.current) clearTimeout(navigateTimerRef.current)
    }
  }, [])

  useEffect(() => {
    const target = saveActionAnchorRef.current
    if (!target) return

    const updateSaveActionVisibility = () => {
      const rect = target.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const nearPageBottom = window.scrollY + viewportHeight >= document.documentElement.scrollHeight - 140
      const anchorReached = rect.top <= viewportHeight - 88

      setIsSaveActionVisible(anchorReached || nearPageBottom)
    }

    updateSaveActionVisibility()
    window.addEventListener('scroll', updateSaveActionVisibility, { passive: true })
    window.addEventListener('resize', updateSaveActionVisibility)

    return () => {
      window.removeEventListener('scroll', updateSaveActionVisibility)
      window.removeEventListener('resize', updateSaveActionVisibility)
    }
  }, [selectedWasteTypes.length, notes, items])

  const handleAddWasteType = (wasteTypeId: number) => {
    if (!wasteTypeId || items.some((item) => item.waste_type_id === wasteTypeId)) {
      return
    }

    setItems((prev) => [...prev, buildEmptyItem(wasteTypeId)])
    setWasteTypeSearch('')
    setActiveEditorWasteTypeId(wasteTypeId)
  }

  const handleRecoverDraft = () => {
    if (!recoverableDraft) return

    setRecordedTime(recoverableDraft.recordedTime)
    setFinishedTime(recoverableDraft.finishedTime)
    setNotes(recoverableDraft.notes)
    setItems(recoverableDraft.items)
    setRecordPhotos([])
    setRecoverableDraft(null)
  }

  const handleOpenItemEditor = (wasteTypeId: number) => {
    setActiveEditorWasteTypeId(wasteTypeId)
  }

  const handleCloseItemEditor = () => {
    if (activeEditorWasteTypeId) {
      const activeItem = itemsByWasteTypeId[activeEditorWasteTypeId]

      if (
        activeItem &&
        (activeItem.kilos <= 0 || activeItem.segregation_status === '')
      ) {
        setItems((prev) =>
          prev.filter((item) => item.waste_type_id !== activeEditorWasteTypeId)
        )
      }
    }

    setActiveEditorWasteTypeId(null)
  }

  const handleGoToFinalStep = () => {
    notesSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  const handleRemoveItem = (wasteTypeId: number) => {
    setItems((prev) =>
      prev.filter((item) => {
        if (item.waste_type_id !== wasteTypeId) {
          return true
        }

        item.photos.filter(isLocalPhoto).forEach((photo) => URL.revokeObjectURL(photo.url))
        return false
      })
    )
  }

  const handleKilosChange = (wasteTypeId: number, value: string) => {
    const kilos = parseFloat(value) || 0

    setItems((prev) =>
      prev.map((item) =>
        item.waste_type_id === wasteTypeId ? { ...item, kilos } : item
      )
    )
  }

  const handleSegregationChange = (
    wasteTypeId: number,
    segregationStatus: SegregationStatus
  ) => {
    setItems((prev) =>
      prev.map((item) =>
        item.waste_type_id === wasteTypeId
          ? { ...item, segregation_status: segregationStatus }
          : item
      )
    )
  }

  const handleItemNotesChange = (wasteTypeId: number, value: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.waste_type_id === wasteTypeId ? { ...item, notes: value } : item
      )
    )
  }

  const handleItemPhotoCapture = useCallback(
    (wasteTypeId: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? [])

      setItems((prev) =>
        prev.map((item) => {
          if (item.waste_type_id !== wasteTypeId) {
            return item
          }

          const remaining = MAX_ITEM_PHOTOS - item.photos.length
          const filesToAdd = files.slice(0, remaining)

          return {
            ...item,
            photos: [
              ...item.photos,
              ...filesToAdd.map((file) => ({
                source: 'local' as const,
                blob: file as Blob,
                url: URL.createObjectURL(file),
              })),
            ],
          }
        })
      )

      e.target.value = ''
    },
    []
  )

  const removeItemPhoto = (wasteTypeId: number, photoIndex: number) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.waste_type_id !== wasteTypeId) {
          return item
        }

        const photo = item.photos[photoIndex]
        if (photo && isLocalPhoto(photo)) {
          URL.revokeObjectURL(photo.url)
        }

        return {
          ...item,
          photos: item.photos.filter((_, idx) => idx !== photoIndex),
        }
      })
    )
  }

  const totalKilos = items.reduce((sum, item) => sum + Number(item.kilos), 0)
  const hasItems = items.some((item) => item.kilos > 0)
  const hasValidItems = items
    .filter((item) => item.kilos > 0)
    .every((item) => item.segregation_status !== '')
  const hasScheduledDate = Boolean(activePickup?.scheduled_date)
  const startedAt = activePickup?.scheduled_date ? buildPickupDateTime(activePickup.scheduled_date, recordedTime) : ''
  const finishedAt = activePickup?.scheduled_date ? buildPickupDateTime(activePickup.scheduled_date, finishedTime) : ''
  const isFinishedAfterStarted =
    Boolean(startedAt && finishedAt) && new Date(finishedAt) > new Date(startedAt)
  const saveReadiness = useMemo(() => {
    if (!hasScheduledDate) {
      return { canSave: false, message: 'No se pudo determinar la fecha del retiro.' }
    }

    if (!hasItems) {
      return { canSave: false, message: 'Agrega al menos un residuo con kilos para guardar.' }
    }

    if (!hasValidItems) {
      return { canSave: false, message: 'Cada residuo con kilos debe tener calidad de segregación.' }
    }

    if (!recordedTime) {
      return { canSave: false, message: 'Ingresa la hora de inicio para guardar.' }
    }

    if (!finishedTime) {
      return { canSave: false, message: 'Ingresa la hora de término para guardar.' }
    }

    if (!isFinishedAfterStarted) {
      return { canSave: false, message: 'La hora de término debe ser posterior a la hora de inicio.' }
    }

    return { canSave: true, message: null }
  }, [hasScheduledDate, hasItems, hasValidItems, recordedTime, finishedTime, isFinishedAfterStarted])
  const isSaveDisabled = !saveReadiness.canSave || isSaving || saveSuccess
  const saveButtonLabel = savedDraftId && !isOnline
    ? 'Actualizar borrador'
    : serverRecordId
      ? 'Actualizar registro'
      : isOnline
        ? 'Guardar registro'
        : 'Guardar sin conexión'
  const activeEditorWasteType = activeEditorWasteTypeId
    ? wasteTypesById[activeEditorWasteTypeId]
    : null
  const activeEditorItem = activeEditorWasteTypeId
    ? itemsByWasteTypeId[activeEditorWasteTypeId]
    : null

  const validRecordItems = items
    .filter(
      (item): item is ValidRecordFormItem =>
        item.kilos > 0 && item.segregation_status !== ''
    )
  const buildSubmitPayload = (): SubmitRecordPayload => ({
    pickup_id: pickupIdNum,
    local_uuid: getOrCreateLocalUuid(),
    started_at: startedAt,
    finished_at: finishedAt,
    notes,
    items: validRecordItems.map((item) => ({
      waste_type_id: item.waste_type_id,
      kilos: item.kilos,
      segregation_status: item.segregation_status,
      notes: item.notes,
    })),
  })
  const saveLocalDraft = async (payload: SubmitRecordPayload, itemsWithPhotos: ValidRecordFormItem[]) => {
    const draftData = {
      ...payload,
      items: itemsWithPhotos.map((item) => ({
        waste_type_id: item.waste_type_id,
        kilos: item.kilos,
        segregation_status: item.segregation_status,
        notes: item.notes,
        photos: item.photos.filter(isLocalPhoto).map((photo) => photo.blob),
      })),
      pending_sync: 1 as const,
      updated_at: new Date().toISOString(),
    }

    if (savedDraftId) {
      await updateDraft(savedDraftId, draftData)
    } else {
      const newId = await saveDraft(draftData)
      setSavedDraftId(newId)
    }
  }

  const handleSave = async () => {
    if (!saveReadiness.canSave) {
      setSaveError(saveReadiness.message)
      return
    }

    setSaveError(null)
    setIsSaving(true)
    setSaveSuccess(false)
    setLastSaveMode(null)

    try {
      const payload = buildSubmitPayload()

      if (isOnline) {
        let savedRecord
        try {
          savedRecord = serverRecordId
            ? await pickupRecordsApi.update(serverRecordId, payload)
            : await pickupRecordsApi.create(payload)
        } catch (err) {
          if (isNetworkUnavailableError(err)) {
            if (!serverRecordId) {
              await saveLocalDraft(payload, validRecordItems)
              setLastSaveMode('offline')
              setSaveSuccess(true)
              navigateTimerRef.current = setTimeout(() => {
                navigate('/pickups')
              }, 1500)
              return
            }
            setSaveError('Sin conexión. Reconéctate para actualizar el registro.')
            return
          }

          throw err
        }

        if (!serverRecordId) {
          setServerRecordId(savedRecord.data.id)
        }

        const serverItemsByWasteTypeId = Object.fromEntries(
          (savedRecord.data.items ?? []).map((item) => [item.waste_type_id, item])
        )
        const photoUploads = validRecordItems.flatMap((item) => {
          const localPhotos = item.photos.filter(isLocalPhoto)
          if (localPhotos.length === 0) return []
          const serverItemId = serverItemsByWasteTypeId[item.waste_type_id]?.id ?? null
          return localPhotos.map((photo) =>
            pickupRecordsApi.uploadPhoto(savedRecord.data.id, photo.blob, serverItemId).catch((err) => {
              console.error('Error subiendo foto:', err)
            })
          )
        })
        if (photoUploads.length > 0) {
          await Promise.allSettled(photoUploads)
        }

        await queryClient.invalidateQueries({ queryKey: pickupRecordKeys.list(pickupIdNum) })
        await queryClient.invalidateQueries({ queryKey: pickupKeys.detail(pickupIdNum) })
        await queryClient.invalidateQueries({ queryKey: pickupKeys.lists() })

        // Eliminar el borrador local para que el detalle y la edición lean la
        // misma fuente (el registro del servidor) y no muestren datos divergentes.
        if (savedDraftId) {
          await deleteDraft(savedDraftId).catch((err) =>
            console.error('Error eliminando borrador local:', err)
          )
          setSavedDraftId(null)
        }

        setLastSaveMode('online')
      } else {
        await saveLocalDraft(payload, validRecordItems)
        setLastSaveMode('offline')
      }

      setSaveSuccess(true)

      navigateTimerRef.current = setTimeout(() => {
        navigate('/pickups')
      }, 1500)
    } catch (err) {
      const parsed = parseSyncError(err, 'No se pudo guardar el registro. Inténtalo nuevamente.')
      reportSyncError({ kind: 'pickup', draftId: savedDraftId ?? undefined, parsed })
      setSaveError(parsed.userMessage)
    } finally {
      setIsSaving(false)
    }
  }

  const renderSaveButtonContent = () => {
    if (isSaving) {
      return (
        <>
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Guardando...
        </>
      )
    }

    if (saveSuccess) {
      return (
        <>
          <CheckCircle2 className="mr-2 h-5 w-5" />
          ¡Guardado!
        </>
      )
    }

    return (
      <>
        <Save className="mr-2 h-5 w-5" />
        {saveButtonLabel}
      </>
    )
  }

  if (loadingDraft || loadingPickup || loadingCachedPickup || (isOnline && !recoverableDraft && loadingPickupRecords)) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/pickups')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Registro en terreno</h1>
          <p className="text-sm text-muted-foreground">
            {activePickup?.client?.trade_name ?? `Retiro #${pickupIdNum}`}
            {activePickup?.scheduled_date &&
              ` — ${format(new Date(activePickup.scheduled_date + 'T00:00:00'), "d 'de' MMMM yyyy", { locale: es })}`}
          </p>
        </div>

        <div
          className={cn(
            'flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium',
            isOnline
              ? 'bg-green-100 text-green-700'
              : 'bg-orange-100 text-orange-700'
          )}
        >
          {isOnline ? (
            <Wifi className="h-3 w-3" />
          ) : (
            <WifiOff className="h-3 w-3" />
          )}
          {isOnline ? 'En línea' : 'Sin conexión'}
        </div>
      </div>

      {!isOnline && (
        <div className="rounded-md border border-orange-200 bg-orange-50 px-4 py-3">
          <p className="text-sm font-medium text-orange-800">
            Modo offline activo
          </p>
          <p className="mt-0.5 text-xs text-orange-700">
            El registro se guardará localmente y se sincronizará cuando
            recuperes la conexión a internet.
          </p>
        </div>
      )}

      {saveSuccess && (
        <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-4 py-3">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <p className="text-sm text-green-800">
            Registro guardado correctamente.{' '}
            {lastSaveMode === 'online'
              ? 'Se sincronizará automáticamente.'
              : 'Se sincronizará cuando tengas conexión.'}
          </p>
        </div>
      )}

      {saveError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-800">{saveError}</p>
        </div>
      )}

      {recoverableDraft && (
        <div className="rounded-md border border-sky-200 bg-sky-50 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-sky-900">
                Hay un borrador previo para este retiro
              </p>
              <p className="mt-0.5 text-xs text-sky-800">
                Se cargó automáticamente para que puedas seguir editándolo.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              className="border-sky-200 bg-white text-sky-900 hover:bg-sky-100"
              onClick={handleRecoverDraft}
            >
              Cargar borrador
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Kilos por tipo de residuo</CardTitle>
          <CardDescription>
            Agrega solo los residuos necesarios para este registro. Cada ítem con kilos debe incluir calidad de segregación.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {availableWasteTypes.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-center">
              <PackageOpen className="mb-2 h-8 w-8 text-gray-300" />
              <p className="text-sm text-gray-400">
                {hasClientRestrictions
                  ? 'Este cliente no tiene tipos de residuo disponibles'
                  : 'No hay tipos de residuo configurados'}
              </p>
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 shadow-sm">
                  <div className="flex flex-col gap-3">
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        Catálogo de residuos
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Busca y agrega residuos como en una app de pedidos: un toque para sumar al registro.
                      </p>
                    </div>

                    <Input
                      value={wasteTypeSearch}
                      onChange={(e) => setWasteTypeSearch(e.target.value)}
                      placeholder="Buscar por categoría, subcategoría o residuo"
                      className="h-11 bg-white"
                    />

                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Plus className="h-3.5 w-3.5" />
                      {selectedWasteTypes.length} agregado{selectedWasteTypes.length === 1 ? '' : 's'} de {availableWasteTypes.length}
                    </div>
                  </div>
                </div>

                {filteredCatalogWasteTypes.length > 0 ? (
                  <div className="space-y-4">
                    {wasteTypesCatalog.map((categoryGroup) => (
                      <div
                        key={categoryGroup.categoryName}
                        className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm"
                      >
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">
                              {categoryGroup.categoryName}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {categoryGroup.subcategories.reduce(
                                (count, subcategory) => count + subcategory.wasteTypes.length,
                                0
                              )} opciones disponibles
                            </p>
                          </div>
                        </div>

                        <div className="space-y-3">
                          {categoryGroup.subcategories.map((subcategory) => (
                            <div key={`${categoryGroup.categoryName}-${subcategory.subcategoryName}`}>
                              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                                {subcategory.subcategoryName}
                              </p>
                              <div className="space-y-2">
                                {subcategory.wasteTypes.map((wasteType) => (
                                  (() => {
                                    const existingItem = itemsByWasteTypeId[wasteType.id]
                                    const isAdded = Boolean(existingItem)
                                    const segregationOption = getSegregationOption(
                                      existingItem?.segregation_status ?? ''
                                    )

                                    return (
                                      <button
                                        key={wasteType.id}
                                        type="button"
                                        onClick={() =>
                                          isAdded
                                            ? handleOpenItemEditor(wasteType.id)
                                            : handleAddWasteType(wasteType.id)
                                        }
                                        className={cn(
                                          'flex w-full items-center justify-between rounded-xl border px-3 py-3 text-left transition-colors',
                                          isAdded
                                            ? 'border-green-200 bg-green-50 hover:bg-green-100'
                                            : 'border-slate-200 bg-slate-50 hover:border-[#b9d94a] hover:bg-[#f4f8d4]'
                                        )}
                                      >
                                        <div className="min-w-0 pr-3">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <p className="truncate text-sm font-medium text-slate-900">
                                              {wasteType.name}
                                            </p>
                                            {isAdded && (
                                              <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                                                Agregado
                                              </span>
                                            )}
                                          </div>
                                          <p className="mt-0.5 text-xs text-muted-foreground">
                                            {subcategory.subcategoryName} · {wasteType.unit}
                                          </p>
                                          {isAdded && existingItem && (
                                            <div className="mt-2 flex flex-wrap gap-2">
                                              {existingItem.kilos > 0 && (
                                                <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-700">
                                                  {Number(existingItem.kilos).toFixed(1)} kg
                                                </span>
                                              )}
                                              {segregationOption && (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-700">
                                                  <segregationOption.icon className="h-3.5 w-3.5 shrink-0" />
                                                  {segregationOption.label}
                                                </span>
                                              )}
                                              {existingItem.photos.length > 0 && (
                                                <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-700">
                                                  {existingItem.photos.length} foto{existingItem.photos.length === 1 ? '' : 's'}
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                        <span
                                          className={cn(
                                            'flex h-9 w-9 items-center justify-center rounded-full border transition-colors',
                                            isAdded
                                              ? 'border-slate-200 bg-white text-slate-700'
                                              : 'border-[#383f2d] bg-[#383f2d] text-[#dff24b]'
                                          )}
                                          aria-hidden="true"
                                        >
                                          {isAdded ? (
                                            <Pencil className="h-4 w-4" />
                                          ) : (
                                            <Plus className="h-4 w-4" />
                                          )}
                                        </span>
                                      </button>
                                    )
                                  })()
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-center">
                    <p className="text-sm font-medium text-slate-700">
                      No encontramos residuos para esa búsqueda
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Prueba con otra categoría, subcategoría o nombre.
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-4 lg:sticky lg:top-4">
                <div
                  ref={notesSectionRef}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-900">Registro actual</p>
                      <p className="text-xs text-muted-foreground">
                        Completa solo los residuos que agregaste arriba.
                      </p>
                    </div>
                    {selectedWasteTypes.length > 0 && (
                      <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                        {selectedWasteTypes.length} en registro
                      </div>
                    )}
                  </div>

                  <div className="mt-4 space-y-3">
                    <div className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="recorded-at">Hora de inicio</Label>
                        <Input
                          id="recorded-at"
                          type="time"
                          value={recordedTime}
                          onChange={(e) => setRecordedTime(e.target.value)}
                          className="bg-white"
                          step={60}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="finished-at">Hora de término</Label>
                        <Input
                          id="finished-at"
                          type="time"
                          value={finishedTime}
                          onChange={(e) => setFinishedTime(e.target.value)}
                          min={recordedTime}
                          required
                          className="bg-white"
                          step={60}
                        />
                      </div>
                      <p className="sm:col-span-2 text-xs text-muted-foreground">
                        La fecha corresponde al retiro programado: {activePickup?.scheduled_date
                          ? format(new Date(activePickup.scheduled_date + 'T00:00:00'), "d 'de' MMMM yyyy", { locale: es })
                          : 'sin fecha'}
                      </p>
                    </div>

                    {selectedWasteTypes.length === 0 ? (
                      <div className="flex flex-col items-center rounded-xl border border-slate-200 py-8 text-center">
                        <PackageOpen className="mb-2 h-8 w-8 text-gray-300" />
                        <p className="text-sm font-medium text-slate-700">
                          Aún no agregas residuos al registro
                        </p>
                        <p className="mt-1 max-w-xs text-xs text-muted-foreground">
                          Usa el catálogo superior y toca `Agregar` en cada residuo que corresponda.
                        </p>
                      </div>
                    ) : (
                      selectedWasteTypes.map((wasteType) => {
                        const item = itemsByWasteTypeId[wasteType.id]

                        if (!item) return null

                        const hasContent =
                          item.kilos > 0 ||
                          item.segregation_status !== '' ||
                          item.notes.trim().length > 0 ||
                          item.photos.length > 0

                        const segregationOption = getSegregationOption(item.segregation_status)

                        return (
                          <div
                            key={wasteType.id}
                            className={cn(
                              'rounded-2xl border p-3 shadow-sm transition-colors',
                              hasContent
                                ? 'border-green-200 bg-green-50/50'
                                : 'border-slate-200 bg-white'
                            )}
                          >
                            <div className="flex items-start gap-3">
                              <div className="min-w-0 flex-1">
                                <Label className="text-sm font-medium">
                                  {wasteType.name}
                                </Label>
                                <div className="mt-2 flex flex-wrap gap-2">
                                  <span
                                    className={cn(
                                      'rounded-full px-2.5 py-1 text-xs font-medium',
                                      hasContent
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-slate-100 text-slate-600'
                                    )}
                                  >
                                    {hasContent ? 'Con contenido' : 'Pendiente'}
                                  </span>
                                  {item.kilos > 0 && (
                                    <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-700">
                                      {Number(item.kilos).toFixed(1)} kg
                                    </span>
                                  )}
                                  {segregationOption && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-700">
                                      <segregationOption.icon className="h-3.5 w-3.5 shrink-0" />
                                      {segregationOption.label}
                                    </span>
                                  )}
                                  {item.photos.length > 0 && (
                                    <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-700">
                                      {item.photos.length} foto{item.photos.length === 1 ? '' : 's'}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleOpenItemEditor(wasteType.id)}
                                  className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-100 hover:text-slate-700"
                                  aria-label={`Editar ${wasteType.name}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveItem(wasteType.id)}
                                  className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                                  aria-label={`Quitar ${wasteType.name}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        )
                      })
                    )}

                    {recordPhotos.length > 0 && (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-slate-900">Fotos del retiro</p>
                            <p className="text-xs text-muted-foreground">
                              Evidencia ya sincronizada sin residuo específico.
                            </p>
                          </div>
                          <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-700">
                            {recordPhotos.length} foto{recordPhotos.length === 1 ? '' : 's'}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {recordPhotos.map((photo, index) => (
                            <a
                              key={photo.id}
                              href={photo.url}
                              target="_blank"
                              rel="noreferrer"
                              aria-label={`Abrir foto sincronizada del retiro ${index + 1}`}
                              className="relative aspect-square overflow-hidden rounded-lg border bg-white transition-opacity hover:opacity-80"
                            >
                              <img
                                src={photo.url}
                                alt={`Foto sincronizada del retiro ${index + 1}`}
                                className="h-full w-full object-cover"
                              />
                              <span className="absolute bottom-1 left-1 rounded-full bg-white/95 px-1.5 py-0.5 text-[10px] font-medium text-green-700 shadow-sm">
                                Sincronizada
                              </span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 border-t border-slate-200 pt-4">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                      Observaciones
                    </Label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Condiciones del retiro, incidencias, notas relevantes..."
                      rows={4}
                      className="mt-2"
                    />

                    <div ref={saveActionAnchorRef} className="h-px" aria-hidden="true" />

                    <div className="mt-4 hidden lg:flex">
                      <div className="space-y-2">
                        <Button
                          className={cn(
                            'h-11 px-5 text-sm font-semibold shadow-lg',
                            saveReadiness.canSave ? '' : 'cursor-not-allowed bg-gray-300'
                          )}
                          disabled={isSaveDisabled}
                          onClick={handleSave}
                        >
                          {renderSaveButtonContent()}
                        </Button>
                        {!saveReadiness.canSave && (
                          <p className="max-w-xs text-xs text-muted-foreground">
                            {saveReadiness.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {totalKilos > 0 && (
            <div className="mt-3 flex items-center justify-between border-t pt-3">
              <span className="text-sm font-medium text-gray-700">
                Total registrado
              </span>
              <span className="text-lg font-bold text-green-700">
                {totalKilos.toFixed(1)} kg
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(activeEditorWasteType && activeEditorItem)}
        onOpenChange={(open) => {
          if (!open) handleCloseItemEditor()
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          {activeEditorWasteType && activeEditorItem && (
            <>
              <DialogHeader>
                <DialogTitle>{activeEditorWasteType.name}</DialogTitle>
                <DialogDescription>
                  Completa los datos de este residuo sin salir del listado.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="rounded-xl bg-slate-50 p-3">
                  <div className="flex items-start gap-3">
                    <div className="w-[92px] shrink-0 space-y-2">
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                        Kilos
                      </Label>
                      <Input
                        type="number"
                        min={0}
                        step={0.1}
                        value={activeEditorItem.kilos === 0 ? '' : activeEditorItem.kilos}
                        onChange={(e) =>
                          handleKilosChange(activeEditorWasteType.id, e.target.value)
                        }
                        placeholder="0"
                        className="h-10 w-[68px] px-2 text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                    </div>

                    <div className="min-w-0 flex-1 space-y-2">
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                        Calidad
                      </Label>
                      <div className="grid grid-cols-3 gap-2">
                        {SEGREGATION_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() =>
                              handleSegregationChange(activeEditorWasteType.id, option.value)
                            }
                            className={cn(
                              'min-w-0 rounded-lg border px-2 py-2 text-xs font-medium transition-colors sm:px-3 sm:text-sm',
                              activeEditorItem.segregation_status === option.value
                                ? option.selectedClassName
                                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                            )}
                          >
                            <span className="flex items-center justify-center gap-1.5">
                              <option.icon className="h-4 w-4 shrink-0" />
                              <span>{option.label}</span>
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Observación del ítem
                  </Label>
                  <Textarea
                    value={activeEditorItem.notes}
                    onChange={(e) =>
                      handleItemNotesChange(activeEditorWasteType.id, e.target.value)
                    }
                    placeholder="Observación del ítem..."
                    rows={3}
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                      Fotos del ítem
                    </Label>
                    <span className="text-xs text-muted-foreground">
                      {activeEditorItem.photos.length}/{MAX_ITEM_PHOTOS}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {activeEditorItem.photos.map((photo, photoIndex) => (
                      <div
                        key={photo.source === 'synced' ? `synced-${photo.id}` : photo.url}
                        className="relative aspect-square"
                      >
                        <img
                          src={photo.url}
                          alt={`Foto ${photoIndex + 1}`}
                          className="h-full w-full rounded-lg object-cover"
                        />
                        <span
                          className={cn(
                            'absolute bottom-1 left-1 rounded-full bg-white/95 px-1.5 py-0.5 text-[10px] font-medium shadow-sm',
                            photo.source === 'synced' ? 'text-green-700' : 'text-orange-700'
                          )}
                        >
                          {photo.source === 'synced' ? 'Sincronizada' : 'Pendiente'}
                        </span>
                        {photo.source === 'local' && (
                          <button
                            type="button"
                            onClick={() =>
                              removeItemPhoto(activeEditorWasteType.id, photoIndex)
                            }
                            className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow-sm hover:bg-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    ))}

                    {activeEditorItem.photos.length < MAX_ITEM_PHOTOS && (
                      <>
                        <label className="flex aspect-square cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 text-gray-400 transition-colors hover:border-green-400 hover:text-green-500">
                          <Camera className="h-6 w-6" />
                          <span className="mt-1 text-center text-[11px]">Cámara</span>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) =>
                              handleItemPhotoCapture(activeEditorWasteType.id, e)
                            }
                          />
                        </label>
                        <label className="flex aspect-square cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 text-gray-400 transition-colors hover:border-green-400 hover:text-green-500">
                          <ImagePlus className="h-6 w-6" />
                          <span className="mt-1 text-center text-[11px]">Galería</span>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={(e) =>
                              handleItemPhotoCapture(activeEditorWasteType.id, e)
                            }
                          />
                        </label>
                      </>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <Button
                      type="button"
                      disabled={
                        activeEditorItem.kilos <= 0 ||
                        activeEditorItem.segregation_status === ''
                      }
                      onClick={handleCloseItemEditor}
                    >
                      Listo
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <div className="sticky bottom-4 flex items-center gap-3">
        <div className="flex-1 lg:hidden">
          {isSaveActionVisible ? (
            <div className="space-y-2">
              <Button
                className={cn(
                  'h-12 w-full text-base font-semibold shadow-lg',
                  saveReadiness.canSave ? '' : 'cursor-not-allowed bg-gray-300'
                )}
                disabled={isSaveDisabled}
                onClick={handleSave}
              >
                {renderSaveButtonContent()}
              </Button>
              {!saveReadiness.canSave && (
                <p className="rounded-md bg-white/95 px-3 py-2 text-xs text-muted-foreground shadow">
                  {saveReadiness.message}
                </p>
              )}
            </div>
          ) : (
            <Button
              className={cn(
                'h-12 w-full text-base font-semibold shadow-lg',
                hasItems ? '' : 'cursor-not-allowed bg-gray-300'
              )}
              disabled={!hasItems}
              onClick={handleGoToFinalStep}
            >
              Continuar al cierre
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
